<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { ref, watch } from 'vue'
import { acceptIdentificationUserAction } from '@/api/functions/users/accept-identification-user-action'
import { rejectIdentificationUserAction } from '@/api/functions/users/reject-identification-user-action'
import { uploadIdentificationAction } from '@/api/functions/users/upload-identification-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const emit = defineEmits(['refreshUserData'])

const props = defineProps({
  userData: Object,
  uuid: {
    type: String,
    required: true,
  },
})

const toast = useToast()
const queryClient = useQueryClient()

const showRejectModal = ref(false)
const showAcceptModal = ref(false)
const isShowEditSelfi = ref(false)
const previewImage = ref(props.userData?.identifications?.passport?.image || null)
const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const isUploading = ref(false)

watch(
  () => props.userData,
  (newVal) => {
    previewImage.value = newVal?.identifications?.passport?.image || null
  },
  { deep: true, immediate: true }
)

const openImageInNewTab = () => {
  if (previewImage.value) window.open(previewImage.value, '_blank')
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  selectedFile.value = file

  const reader = new FileReader()
  reader.onload = (e) => {
    previewImage.value = e.target?.result as string
  }
  reader.readAsDataURL(file)

  mutation.mutate(file)
}

const mutation = useMutationApi({
  mutationFn: (file: File) =>
    uploadIdentificationAction({
      image: file,
      uuid: props.uuid,
    }),
  onMutate: () => {
    isUploading.value = true
    toast.info('در حال آپلود تصویر...')
  },
  onSuccess: (response) => {
    toast.success(response.data?.meta?.message || 'تصویر با موفقیت آپلود شد')
    queryClient.invalidateQueries({ queryKey: ['user', props.uuid] })
    emit('refreshUserData')
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
  onSettled: () => {
    isUploading.value = false
  },
})

// REJECT  DOCUMENT
const rejectMutation = useMutationApi({
  mutationFn: (id: string) => rejectIdentificationUserAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'مدرک هویتی با موفقیت رد شد')
    emit('refreshUserData')
    showRejectModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleRejectDocument = () => {
  if (!props.uuid) return
  rejectMutation.mutate(props.uuid)
}

// ACCEPT  DOCUMENT
const acceptMutation = useMutationApi({
  mutationFn: (id: string) => acceptIdentificationUserAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'مدرک هویتی با موفقیت تایید شد')
    emit('refreshUserData')
    showAcceptModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleAcceptDocument = () => {
  if (!props.uuid) return
  acceptMutation.mutate(props.uuid)
}
</script>

<template>
    <div class="bg-white rounded-xl grid lg:grid-cols-3 my-6 lg:min-h-28 p-8 gap-6">

        <ConfirmModal v-model="showRejectModal" title="رد مدرک هویتی" :message="'آیا از رد مدرک هویتی اطمینان دارید؟'"
            confirm-text="بله رد شود" cancel-text="بازگشت" @confirm="handleRejectDocument"
            @cancel="showRejectModal = false" />

        <ConfirmModal v-model="showAcceptModal" title="تایید مدرک هویتی"
            :message="'آیا از تایید مدرک هویتی اطمینان دارید؟'" confirm-text="بله تایید شود" cancel-text="بازگشت"
            @confirm="handleAcceptDocument" @cancel="showAcceptModal = false" />

        <div class="w-full flex lg:grid lg:grid-cols-12 items-center justify-between text-sm">
            <div class="col-span-4 text-[#2d5396]">مدرک هویتی:</div>

            <div v-if="userData?.identifications?.passport?.image || isShowEditSelfi"
                class="flex flex-col col-span-8 gap-3 font-fa text-[#1b325a]">
                <img v-if="previewImage" width="201" class="h-[118px]! shadow rounded-xl object-cover"
                    :src="previewImage" />
                <div v-else class="h-[118px]! w-[201px] rounded-xl bg-[#f5f5f5]" />

                <button v-if="!isShowEditSelfi" @click="openImageInNewTab"
                    class="w-fit px-3.5 font-medium cursor-pointer flex gap-1 items-center duration-300 hover:bg-[#eaeef5] text-[#2d5396] h-8 justify-center rounded-lg text-[13px]">
                    <img src="/svg/download-svgrepo-com.svg" width="18" alt="chevron" />
                    دانلود
                </button>

                <Button v-else :isAdmin="true" variant="outline" @click="triggerFileInput" :disabled="isUploading"
                    class="w-fit px-3.5! font-medium cursor-pointer flex gap-1 items-center duration-300 h-8! border-2 justify-center rounded-lg! text-[13px]">
                    <span v-if="!isUploading">
                        {{ userData?.identifications?.passport?.image ? 'بارگذاری مجدد' : 'بارگذاری' }}
                    </span>
                    <span v-else>در حال آپلود...</span>
                </Button>


                <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileChange" />
            </div>

            <div v-else class="col-span-8 font-fa text-[#1b325a]">آپلود نشده</div>
        </div>

        <div class="w-full flex items-center justify-between text-sm">
            <span class="text-[#2d5396]">وضعیت تصویر سلفی:</span>
            <span class="font-fa text-[#1b325a]">
                {{ userData?.identifications?.passport?.status === 'VERIFIED' ? 'تایید شده' : 'تایید نشده' }}
            </span>
        </div>

        <div class="flex items-center justify-end">
            <Button @click="isShowEditSelfi = !isShowEditSelfi" :isAdmin="true"
                class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                ویرایش
            </Button>

            <Button @click="showAcceptModal = true"
                :disabled="userData?.identifications?.passport?.status === 'VERIFIED' || !userData?.identifications?.passport?.image"
                :isAdmin="true" class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                تایید
            </Button>

            <Button @click="showRejectModal = true" :isAdmin="true"
                :disabled="userData?.identifications?.passport?.status === 'REJECTED' || !userData?.identifications?.passport?.image"
                class=" w-1/2 lg:w-auto border-2 disabled:border-0 disabled:cursor-not-allowed rounded-[10px] px-4 h-10! py-0 text-[13px] font-normal me-2">
                رد
            </Button>

        </div>
    </div>
</template>
